import styled, { css, keyframes } from 'styled-components';

// Keyframes
const columnEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const countPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Status colors
const statusColors = {
  analysis: {
    main: '#8b5cf6',
    glow: 'rgba(139, 92, 246, 0.4)',
    gradient: 'linear-gradient(180deg, rgba(139, 92, 246, 0.15) 0%, transparent 100%)'
  },
  production: {
    main: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.4)',
    gradient: 'linear-gradient(180deg, rgba(245, 158, 11, 0.15) 0%, transparent 100%)'
  },
  ready: {
    main: '#10b981',
    glow: 'rgba(16, 185, 129, 0.4)',
    gradient: 'linear-gradient(180deg, rgba(16, 185, 129, 0.15) 0%, transparent 100%)'
  },
  completed: {
    main: '#06b6d4',
    glow: 'rgba(6, 182, 212, 0.4)',
    gradient: 'linear-gradient(180deg, rgba(6, 182, 212, 0.15) 0%, transparent 100%)'
  }
};

// Helper to get status styles
const getStatusColor = (status) => statusColors[status] || statusColors.analysis;

export const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${columnEnter} 0.4s ease-out backwards;
  animation-delay: ${props => props.$animationDelay || '0ms'};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  height: fit-content;
  /* Usa toda a altura disponível abaixo do topo, deixando ~40px de respiro */
  max-height: calc(100vh - 200px);

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04);
  }

  @media (max-width: 1500px) {
    border-radius: 12px;
    max-height: calc(100vh - 170px);
  }

  @media (max-height: 800px) {
    max-height: calc(100vh - 160px);
  }
`;

export const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${props => getStatusColor(props.$status).gradient};
  border-top: 3px solid ${props => getStatusColor(props.$status).main};

  @media (max-width: 1500px) {
    padding: 12px 14px;
  }
`;

export const ColumnTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ColumnIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => getStatusColor(props.$status).main}20;
  color: ${props => getStatusColor(props.$status).main};
  font-size: 16px;

  @media (max-width: 1500px) {
    width: 26px;
    height: 26px;
    font-size: 14px;
    border-radius: 7px;
  }
`;

export const ColumnTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #1a1a2e;
  margin: 0;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 1500px) {
    font-size: 12px;
    letter-spacing: 0.3px;
  }
`;

export const ColumnCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border-radius: 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: ${props => getStatusColor(props.$status).main};
  box-shadow: 0 2px 8px ${props => getStatusColor(props.$status).glow};
  transition: all 0.25s ease;

  ${props => props.$hasOrders && css`
    animation: ${countPulse} 2s ease-in-out infinite;
  `}

  @media (max-width: 1500px) {
    min-width: 26px;
    height: 26px;
    padding: 0 8px;
    font-size: 12px;
    border-radius: 8px;
  }
`;

export const ColumnContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  /* Cresce com a coluna; ColumnWrapper segura o limite superior */
  max-height: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8fafc;

  @media (max-width: 1500px) {
    padding: 8px;
    gap: 6px;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  min-height: 200px;
  background: white;
  border-radius: 12px;
  border: 2px dashed rgba(0, 0, 0, 0.08);

  @media (max-width: 1500px) {
    padding: 18px 16px;
    min-height: 120px;
    border-radius: 10px;
  }
`;

export const EmptyIllustrationWrapper = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  opacity: 0.85;
  transition: opacity 0.3s ease;

  ${EmptyState}:hover & {
    opacity: 1;
  }

  @media (max-width: 1500px) {
    width: 44px;
    height: 44px;
    margin-bottom: 6px;

    & svg {
      width: 100%;
      height: 100%;
    }
  }
`;

export const EmptyTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 8px 0;

  @media (max-width: 1500px) {
    font-size: 12px;
    margin: 0 0 4px 0;
  }
`;

export const EmptyDescription = styled.p`
  font-size: 13px;
  color: #94a3b8;
  max-width: 200px;
  line-height: 1.5;
  margin: 0;

  @media (max-width: 1500px) {
    font-size: 11px;
    line-height: 1.35;
    max-width: 180px;
  }
`;

// Config section for analysis column
export const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  margin: 12px 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  @media (max-width: 1500px) {
    padding: 8px 10px;
    margin: 6px 8px;
    gap: 4px;
    border-radius: 8px;
  }
`;

export const ConfigRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const ConfigLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;

  svg {
    font-size: 16px;
    color: #94a3b8;
  }
`;

export const ConfigValue = styled.span`
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #318CD5;
  padding: 3px 10px;
  background: rgba(49, 140, 213, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(49, 140, 213, 0.15);
`;

export const ConfigEditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 36px;
  margin-top: 8px;
  background: transparent;
  border: 1px solid #318CD5;
  border-radius: 8px;
  color: #318CD5;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #318CD5;
    color: white;
  }

  svg {
    font-size: 14px;
  }
`;
